import { Col, Row } from "react-bootstrap";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import ImageInput from "@/components/ImageInput/Index";
import RenderToast from "@/components/RenderToast";
import { achievementBadgesApi } from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import { AchievementInitialValues } from "@/data/WebDataManagement/MasterData/Achievements/initial-values";
import AchievementSchema from "@/schema/WebDataManagement/MasterData/Achievements/AchievementSchema";

const AchievementModal = forwardRef((_, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id
    ? `${achievementBadgesApi}/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Achievement ${action} successfully.`,
    });
  };
  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${achievementBadgesApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: achievementBadgesApi,
    isFormData: true,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: AchievementInitialValues(modalState.id ? showData : null),
    validationSchema: AchievementSchema(!!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) formData.append("_method", "PUT");
    formData.append("amount", values.amount);
    if (values.icon) formData.append("badge", values.icon);

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Achievement Badge" : ""}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            type="number"
            formik={formik}
            fieldName={"amount"}
            saveError={saveError}
            label="Amount"
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <ImageInput
            id="icon"
            formFormik={formik}
            fieldName="icon"
            label="Badge"
            error={saveError}
            existingImageUrl={modalState.id && showData?.data?.badge_url}
          />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default AchievementModal;
